import { Timestamp } from 'firebase/firestore';

/**
 * Tipos de solicitação do módulo SI.
 * Cada tipo tem regras próprias de bloco de acessos e geração de movimentações,
 * configuradas em ConfigTipoSolicitacao (ver config.types.ts).
 */
export type TipoSolicitacao =
  | 'admissao'
  | 'desligamento'
  | 'mudanca_projeto'
  | 'nova_demanda'
  | 'troca_equipamento'
  | 'devolucao'
  | 'pedido_colaborador'
  | 'formatacao'
  | 'acessos'
  | 'divergencia_inventario';

/**
 * Formatação de equipamento e Acessos: são um chamado pro SI (formatar equipamento,
 * criar/resetar acesso a um sistema), não envolvem remessa/logística de equipamento —
 * nunca nascem com nenhuma Movimentação. Usado por `NovaSolicitacaoContent` (não pede
 * campos de equipamento) — ver também `solicitacaoService.updateOperacional`, que cobre
 * o avanço 'aberta' -> 'em_andamento' pra esses tipos (nunca passam pelo mecanismo
 * equivalente de Movimentação, `avancarParaEmAndamentoSeNecessario`).
 */
export const TIPOS_SOLICITACAO_SEM_MOVIMENTACAO: TipoSolicitacao[] = ['formatacao', 'acessos', 'divergencia_inventario'];

/** Tipos sem Cliente/projeto (clienteId null) — reforçado em firestore.rules. Divergência de
 * inventário é o colaborador avisando o SI que o Inventário não bate com o que ele tem
 * (aba Meus equipamentos do Portal do SI): só texto, o SI corrige e conclui. */
export const TIPOS_SOLICITACAO_SEM_CLIENTE: TipoSolicitacao[] = ['acessos', 'divergencia_inventario'];

/**
 * Tipos com requisitos estruturais idênticos — colaborador obrigatório (nunca
 * `colaboradorPendente`), cliente obrigatório, sempre têm Movimentação, nenhum item do
 * catálogo de Acessos se aplica a eles (ver CATALOGO_ACESSOS). Só entre esses a troca de
 * Tipo de Solicitação é permitida depois de criada (EditarTipoSolicitacaoModal), sem
 * deixar Acessos/Movimentação/colaboradorId inconsistentes. Ficam de fora: admissao/
 * desligamento (usam catálogo de Acessos diferente entre si, e só admissao aceita
 * colaboradorId nulo) e formatacao/acessos (nem têm Movimentação). Espelhado em
 * firestore.rules (`siTipoSolicitacaoCompativel`) — mudar aqui exige mudar lá também.
 */
export const GRUPO_TIPO_SOLICITACAO_COMPATIVEL: TipoSolicitacao[] = [
  'mudanca_projeto',
  'nova_demanda',
  'troca_equipamento',
  'devolucao',
  'pedido_colaborador',
];

export type StatusSolicitacao = 'aberta' | 'em_andamento' | 'concluida' | 'cancelada' | 'excluida';

/** Motivo estruturado da exclusão — dropdown fechado (não texto livre) pra manter os
 * números de dashboards/relatórios confiáveis mesmo depois de limpar solicitações
 * geradas por bug/erro operacional (ver ExcluirSolicitacaoModal). */
export type MotivoExclusaoTipo = 'duplicidade' | 'nao_necessaria' | 'aberta_incorretamente' | 'outro';

export type StatusEtapaProcesso = 'pendente' | 'concluido';

export type StatusAcesso = 'pendente' | 'concluido' | 'naoAplicavel';

export type AcaoAcesso = 'criar' | 'revogar' | 'resetar_senha';

export interface AuditoriaAtor {
  id: string;
  name: string;
  avatar?: string | null;
}

export interface SolicitacaoAcesso {
  sistema: string;
  acao: AcaoAcesso;
  status: StatusAcesso;
  concluidoPor?: AuditoriaAtor | null;
  concluidoEm?: Timestamp | null;
}

export interface SolicitacaoKit {
  solicitado: boolean;
  enviadoJuntoComEquipamento: boolean;
  enviadoPeloRH: boolean;
  dataEnvio?: Timestamp | null;
}

/**
 * Snapshot do que foi informado na abertura de uma Admissão para uma pessoa que ainda
 * não existe como colaborador no Radar. Preservado mesmo depois do vínculo (histórico) —
 * nunca sobrescrito pela ação "Vincular Colaborador".
 */
export interface ColaboradorPendente {
  nome: string;
  emailInformado: string;
  /** Cargo/Senioridade informados por quem abre a Admissão (RH) — não vêm do candidato/
   * pré-cadastro. Mesmos campos do perfil do Colaborador (`positionId`/`seniorityId` em
   * CollaboratorPrivate); aplicados automaticamente ao perfil na ação "Vincular
   * Colaborador" (ver onSolicitacaoWrite nas Functions), pra não pedir de novo ao RH. */
  positionId?: string | null;
  positionName?: string | null;
  seniorityId?: string | null;
  seniorityName?: string | null;
}

/**
 * Entidade principal do módulo SI.
 *
 * `acessos` vive aqui (não em Movimentação) porque é propriedade do processo de
 * admissão/desligamento do colaborador como um todo, não de um envio/devolução
 * específico de equipamento.
 */
export interface Solicitacao {
  id: string;
  tipoSolicitacao: TipoSolicitacao;
  status: StatusSolicitacao;

  /**
   * Null só é possível para `tipoSolicitacao === 'admissao'`, enquanto a pessoa ainda não
   * existe como colaborador no Radar (ver `colaboradorPendente`). Imutável depois de
   * definido — a única transição permitida é null → id real, via "Vincular Colaborador"
   * (si_admin), nunca id → outro id nem id → null (Desenho Técnico §10.3, revisado).
   */
  colaboradorId: string | null;
  colaboradorEmail: string;
  /** Snapshot exibido nas listagens (evita mostrar e-mail) — atualizado no vínculo (ver
   * "Vincular Colaborador"), igual a colaboradorEmail. */
  colaboradorNome: string;

  /** Presente só enquanto `colaboradorId` é null — snapshot imutável do que foi informado. */
  colaboradorPendente?: ColaboradorPendente | null;

  /** Vínculo automático (por e-mail pessoal) com um pré-cadastro do Forms de admissão do
   * RH — setado por `onSolicitacaoWrite` (Cloud Function) na criação da Solicitação,
   * nunca pelo client. Ausente quando não há pré-cadastro correspondente. */
  preCadastroId?: string | null;

  solicitanteId: string;
  solicitanteEmail: string;

  /** Snapshot histórico, gravado uma vez na criação — nunca usado para autorização. */
  gestorEmailNoMomentoDaCriacao?: string | null;

  /**
   * Referência à alocação específica (collaborators_private/{colaboradorId}/allocations/{id})
   * a que esta Solicitação se refere — pra que projeto/cliente é o pedido. Usada na
   * autorização de visibilidade de gestor por contexto (ver firestore.rules,
   * siIsResponsavelAlocacaoReferenciada).
   */
  alocacaoReferenciadaId?: string | null;
  /**
   * Snapshot do `Allocation.responsibleId`/`responsibleName` no momento em que a
   * Alocação foi vinculada — não é uma leitura ao vivo (evita "join" entre coleções, que
   * o Firestore não faz numa query de listagem). Fica desatualizado se o Responsável
   * Raro da alocação mudar depois — o SI pode "ressincronizar" a qualquer momento (ver
   * AlocacaoResponsavelSection). Existe pra alimentar a aba "Minha Equipe": um gestor só
   * enxerga a Solicitação se for o Responsável Raro *desta alocação específica*, não do
   * colaborador em geral (decisão de produto — colaborador pode estar em vários projetos
   * com gestores diferentes).
   */
  alocacaoResponsavelId?: string | null;
  /** E-mails dos responsáveis das Movimentações (function onMovimentacaoWriteAlocacao) —
   * "Minha Equipe" e o acesso ao cabeçalho. */
  alocacaoResponsavelEmails?: string[];
  alocacaoResponsavelNome?: string | null;

  /** Null só quando `tipoSolicitacao === 'acessos'` — pedido pontual de sistema interno,
   * não amarrado a um projeto/cliente. Todos os demais tipos continuam exigindo Cliente. */
  clienteId: string | null;

  dataAbertura: Timestamp;
  dataConclusao?: Timestamp | null;

  acessos?: SolicitacaoAcesso[];
  kit?: SolicitacaoKit | null;

  /** Referência a outra Solicitação de origem, quando esta nasce de outra (não usado para reversão de cancelamento). */
  solicitacaoOrigemId?: string | null;
  /** Vínculo manual entre 2 Solicitações relacionadas (ex.: duplicata, mesmo assunto em
   * outro chamado) — só o SI define, a qualquer momento, independente de status. Não é
   * bidirecional (só fica registrado nesta Solicitação, não na apontada) e não afeta
   * autorização/regras de negócio — é só uma referência visível pra quem estiver
   * revisando (ver SolicitacaoRelacionadaSection). */
  solicitacaoRelacionadaId?: string | null;
  /** Texto único antigo — substituído pelas postagens (si_solicitacoes/{id}/comentarios).
   * Mantido só pra exibir o que já existia, somente leitura. */
  observacoes?: string;
  /** Só Troca de Equipamento, Devolução e Periféricos/Acessórios (ver solicitacao.motivo.ts).
   * Texto da opção escolhida; ausente em Solicitações antigas. */
  motivoSolicitacao?: string | null;
  /** Complemento do motivo — obrigatório quando o motivo é "Outro". */
  motivoSolicitacaoDetalhe?: string | null;
  /** Bloco "Dados para o cliente" da abertura (contrato, projeto/equipe, gestor no cliente) —
   * só existe quando o Cliente tem e-mail configurado (ver emailCliente.modelos.ts). O SI
   * pode corrigir depois, na tela do e-mail. */
  dadosCliente?: Record<string, string> | null;

  createdBy: AuditoriaAtor;
  updatedBy?: AuditoriaAtor | null;
  canceledBy?: AuditoriaAtor | null;
  /** Motivo informado por quem cancelou — obrigatório desde a criação deste campo, fica registrado como histórico. */
  motivoCancelamento?: string | null;
  excludedBy?: AuditoriaAtor | null;
  motivoExclusaoTipo?: MotivoExclusaoTipo | null;
  /** Só preenchido quando `motivoExclusaoTipo === 'outro'`. */
  motivoExclusao?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface SolicitacaoCreate {
  tipoSolicitacao: TipoSolicitacao;
  /** Null só quando `tipoSolicitacao === 'admissao'` e `colaboradorPendente` está presente. */
  colaboradorId: string | null;
  colaboradorEmail: string;
  colaboradorNome: string;
  colaboradorPendente?: ColaboradorPendente | null;
  solicitanteId: string;
  solicitanteEmail: string;
  gestorEmailNoMomentoDaCriacao?: string | null;
  alocacaoReferenciadaId?: string | null;
  alocacaoResponsavelId?: string | null;
  alocacaoResponsavelNome?: string | null;
  /** Null só quando `tipoSolicitacao === 'acessos'` (ver comentário em `Solicitacao`). */
  clienteId: string | null;
  observacoes?: string;
  motivoSolicitacao?: string | null;
  motivoSolicitacaoDetalhe?: string | null;
  /** Só usado pelo tipo 'acessos' — sistema/ação escolhidos por quem abre, 1 único item. */
  acessos?: SolicitacaoAcesso[];
  dadosCliente?: Record<string, string> | null;
}

/** "Vincular Colaborador" — só si_admin, só na transição colaboradorId null → id real. */
export interface SolicitacaoVincularColaborador {
  id: string;
  colaboradorId: string;
  colaboradorEmail: string;
  colaboradorNome: string;
  /** Quem executou o vínculo — usado pela Timeline (functions/src/si/onSolicitacaoWrite). */
  updatedBy: AuditoriaAtor;
}

/** Campos de "dados/contexto" — editáveis por SI e Solicitante (ver firestore.rules). */
export interface SolicitacaoUpdate {
  id: string;
  observacoes?: string;
  motivoSolicitacao?: string | null;
  motivoSolicitacaoDetalhe?: string | null;
  /** Corrige erro de digitação — nunca envie null (cliente é obrigatório desde a criação). */
  clienteId?: string;
  /** Só entre os tipos de GRUPO_TIPO_SOLICITACAO_COMPATIVEL, nos dois sentidos (atual e
   * novo) — reforçado em firestore.rules, não só na UI. */
  tipoSolicitacao?: TipoSolicitacao;
}

/** Campos operacionais — exclusivos do SI. */
export interface SolicitacaoOperacionalUpdate {
  id: string;
  alocacaoReferenciadaId?: string | null;
  alocacaoResponsavelId?: string | null;
  alocacaoResponsavelNome?: string | null;
  acessos?: SolicitacaoAcesso[];
  kit?: SolicitacaoKit | null;
  solicitacaoRelacionadaId?: string | null;
}

export interface SolicitacaoCancelamento {
  id: string;
  canceledBy: AuditoriaAtor;
  /** Obrigatório — fica registrado no histórico (Timeline) da Solicitação. */
  motivoCancelamento: string;
}

export interface SolicitacaoReativacao {
  id: string;
}

/** Exclusão (soft-delete) — exclusiva do SI. Não some, só muda de status (ver
 * firestore.rules: `allow delete: if false`, nunca há delete físico do documento). */
export interface SolicitacaoExclusao {
  id: string;
  excludedBy: AuditoriaAtor;
  motivoExclusaoTipo: MotivoExclusaoTipo;
  /** Obrigatório só quando `motivoExclusaoTipo === 'outro'`. */
  motivoExclusao?: string | null;
  /** Só relevante quando `motivoExclusaoTipo === 'duplicidade'`. */
  solicitacaoRelacionadaId?: string | null;
}
